import React, { useState, useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../../utils/scrollbarUtils';
import adminHamburgerIcon from '../../assets/icons/admin/hamburger_icon.png';
import adminDeleteIcon from '../../assets/icons/admin/delete.png';
import languageEnIcon from '../../assets/icons/language/en.png';
import languageCnIcon from '../../assets/icons/language/cn.png';
import languageJaIcon from '../../assets/icons/language/ja.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const CallSettingsBottomSheet = ({ isOpen, onClose, selectedLanguage = 'ko', callReasons = [], onReorder, onEditItem, onShowToast, posType = '' }) => { // eslint-disable-line no-unused-vars
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [scrollbarTop, setScrollbarTop] = useState(0);
  const [scrollbarHeight, setScrollbarHeight] = useState(63);
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const bottomSheetRef = useRef(null);
  const wrapperRef = useRef(null);
  const formContainerRef = useRef(null);
  const scrollbarRef = useRef(null);
  
  // Handle drag to close handlers
  const handleStart = (clientY) => {
    setIsDragging(true);
    setDragStartY(clientY);
    setCurrentY(0);
  };

  const handleMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - dragStartY;
    if (deltaY > 0) { // 아래로만 드래그 가능
      setCurrentY(deltaY);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // 화면 높이의 20% 이상 또는 150px 이상 드래그하면 닫기
    const threshold = Math.min(window.innerHeight * 0.2, 150);
    if (currentY > threshold) {
      // 부드럽게 닫기 애니메이션
      setCurrentY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setCurrentY(0);
      }, 200);
    } else {
      // 원위치로 돌아가기
      setCurrentY(0);
    }
  };

  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientY);
  };
  
  const handleMouseDown = (e) => handleStart(e.clientY);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMove(e.clientY);
      const handleGlobalMouseUp = () => handleEnd();
      const handleGlobalTouchMove = (e) => handleMove(e.touches[0].clientY);
      const handleGlobalTouchEnd = () => handleEnd();

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragStartY, currentY]); // eslint-disable-line react-hooks/exhaustive-deps

  // Menu list states
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]); // 원본 저장용
  const [draggedItem, setDraggedItem] = useState(null);
  const [itemDragStartY, setItemDragStartY] = useState(0);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [originalIndex, setOriginalIndex] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [invalidItems, setInvalidItems] = useState([]);
  const [isNewItem, setIsNewItem] = useState(false);
  const itemRefs = useRef([]);
  const autoScrollRef = useRef(null);
  
  const initialScrollTop = useRef(0);
  const accumulatedScroll = useRef(0);
  const editInputRef = useRef(null);
  
  
  // items 업데이트 - isOpen이 true가 될 때만 초기화
  useEffect(() => {
    if (isOpen) {
      const callItems = callReasons.map((reason, index) => ({
        id: reason.id || `call-${index}`,
        name: reason.text || reason.reason || reason // text 속성 사용, 없으면 reason 또는 문자열 직접 사용
      }));
      setItems(callItems);
      setOriginalItems(callItems); // 원본 저장
    }
  }, [isOpen, callReasons]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      
      // body 스크롤 방지
      lockScroll();
      
      window.history.pushState({ callSettings: true }, '');
      
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      
      setTimeout(() => {
        setIsVisible(false);
        // Reset states
        setEditingItemId(null);
        setEditingText('');
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
      }, 200);
    }
  }, [isOpen]);

  // 페이지 뒤로가기 처리
  // onClose는 ref로 우회 → parent re-render churn에도 listener 1회만 등록
  // cleanup에서 history 좀비 정리 (UI 닫기 경로도 동일)
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (e) => {
      onCloseRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.callSettings) {
        window.history.back();
      }
    };
  }, [isOpen]);  // ← onClose 의존 제거
  

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      // 초기 스케일 설정만 수행
      scaleUI();
    }
  }, [isVisible]);

  // 스크롤바 계산 및 업데이트
  useEffect(() => {
    const updateScrollbar = () => {
      if (formContainerRef.current) {
        const scrollHeight = formContainerRef.current.scrollHeight;
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollTop = formContainerRef.current.scrollTop;
        
        // 스크롤바가 필요한지 확인
        if (scrollHeight <= clientHeight) {
          setShowScrollbar(false); // 스크롤바 숨기기
          return;
        }
        
        // 스크롤바 표시
        setShowScrollbar(true);
        
        // 스크롤바 위치 계산 (고정 21px 높이)
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollTop / scrollableHeight;
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const fixedScrollbarHeight = 63; // 고정 높이 (3배로 증가)
        const maxPosition = trackHeight - fixedScrollbarHeight;
        const scrollbarPosition = scrollPercentage * maxPosition;
        
        setScrollbarHeight(fixedScrollbarHeight);
        setScrollbarTop(scrollbarPosition);
      }
    };

    const handleScroll = (e) => {
      e.stopPropagation(); // 스크롤 이벤트 전파 방지
      if (!isScrollbarDragging) {
        updateScrollbar();
      }
    };

    const formContainer = formContainerRef.current;
    if (formContainer && isVisible) {
      updateScrollbar();
      formContainer.addEventListener('scroll', handleScroll, { passive: false });
      
      // 터치 이벤트도 제어
      const handleTouchMove = (e) => {
        // 드래그 중인 경우에만 스크롤 방지
        if (isDraggingItem) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        formContainer.removeEventListener('scroll', handleScroll);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isVisible, isScrollbarDragging, itemDragStartY, isDraggingItem, items.length]); // items.length 추가

  // items 변경 시 스크롤바 즉시 업데이트
  useEffect(() => {
    if (formContainerRef.current && isVisible) {
      // DOM 업데이트 후 스크롤바 재계산을 위해 setTimeout 사용
      setTimeout(() => {
        const scrollHeight = formContainerRef.current?.scrollHeight || 0;
        const clientHeight = formContainerRef.current?.clientHeight || 0;
        const scrollTop = formContainerRef.current?.scrollTop || 0;
        
        // 스크롤바가 필요한지 확인
        if (scrollHeight <= clientHeight) {
          setShowScrollbar(false);
          return;
        }
        
        // 스크롤바 표시 및 위치 계산
        setShowScrollbar(true);
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollTop / scrollableHeight;
        const trackHeight = clientHeight - 70;
        const fixedScrollbarHeight = 63; // 고정 높이 (3배로 증가)
        const maxPosition = trackHeight - fixedScrollbarHeight;
        const scrollbarPosition = scrollPercentage * maxPosition;
        
        setScrollbarHeight(fixedScrollbarHeight);
        setScrollbarTop(scrollbarPosition);
      }, 0);
    }
  }, [items, isVisible]);

  // 아이템 드래그 이벤트 리스너
  useEffect(() => {
    if (isDraggingItem) {
      const handleMove = (e) => handleItemDragMove(e);
      const handleEnd = (e) => handleItemDragEnd(e);
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDraggingItem, draggedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // 스크롤바 드래그 핸들러
  const handleScrollbarMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsScrollbarDragging(true);
    const startY = e.clientY || e.touches[0].clientY;
    const startTop = scrollbarTop;
    
    const handleScrollbarMove = (e) => {
      const currentY = e.clientY || e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      if (formContainerRef.current) {
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollHeight = formContainerRef.current.scrollHeight;
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const fixedScrollbarHeight = 63; // 고정 높이 (3배로 증가)
        const maxPosition = trackHeight - fixedScrollbarHeight;
        
        // 새 스크롤바 위치 계산
        let newTop = startTop + deltaY;
        newTop = Math.max(0, Math.min(newTop, maxPosition));
        setScrollbarTop(newTop);
        
        // 스크롤 위치 계산
        const scrollPercentage = newTop / maxPosition;
        const newScrollTop = scrollPercentage * (scrollHeight - clientHeight);
        formContainerRef.current.scrollTop = newScrollTop;
      }
    };
    
    const handleScrollbarUp = () => {
      setIsScrollbarDragging(false);
      document.removeEventListener('mousemove', handleScrollbarMove);
      document.removeEventListener('mouseup', handleScrollbarUp);
      document.removeEventListener('touchmove', handleScrollbarMove);
      document.removeEventListener('touchend', handleScrollbarUp);
    };
    
    document.addEventListener('mousemove', handleScrollbarMove);
    document.addEventListener('mouseup', handleScrollbarUp);
    document.addEventListener('touchmove', handleScrollbarMove, { passive: false });
    document.addEventListener('touchend', handleScrollbarUp);
  };

  const handleAddClick = (e) => {
    // 리플 효과 생성
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.className = 'ripple';

    button.appendChild(ripple);

    // hover와 active 상태 강제 해제
    button.style.backgroundColor = '#FFFFFF';
    setTimeout(() => {
      button.blur();
      button.style.backgroundColor = '';
    }, 100);

    // 리플 애니메이션 후 제거
    setTimeout(() => {
      ripple.remove();
    }, 600);

    // 새 아이템을 리스트 맨 뒤에 추가
    const newId = `call-new-${Date.now()}`;
    const newItem = {
      id: newId,
      name: '', // 빈 값으로 설정
      isNew: true
    };

    // 즉시 편집 모드로 설정 (렌더링 전에)
    setEditingItemId(newId);
    setEditingText('');
    setIsNewItem(true);

    // 아이템 추가
    const newItems = [...items, newItem];
    setItems(newItems);

    // requestAnimationFrame으로 DOM 렌더링 후 즉시 처리
    requestAnimationFrame(() => {
      // 맨 아래로 스크롤
      if (formContainerRef.current) {
        formContainerRef.current.scrollTop = formContainerRef.current.scrollHeight;
      }

      // 다음 프레임에서 포커스
      requestAnimationFrame(() => {
        if (editInputRef.current) {
          editInputRef.current.focus();
          editInputRef.current.select();
        }
      });
    });
  };
  
  const handleSaveChanges = (finalItems = null) => {
    // 편집 중인 값을 포함하여 최종 아이템 목록 구성
    let itemsToSave = finalItems || items;

    // 현재 편집 중인 항목이 있다면 그 값 반영
    if (!finalItems && editingItemId) {
      const index = itemsToSave.findIndex(item => item.id === editingItemId);
      if (index !== -1) {
        itemsToSave = [...itemsToSave];
        itemsToSave[index] = {
          ...itemsToSave[index],
          name: editingText.trim() // 빈 값도 포함하여 검증
        };
      }
    }

    // 빈 이름이 있는지 검증
    const emptyItems = [];
    let hasEmptyFields = false;

    itemsToSave.forEach(item => {
      // 편집 중인 항목도 포함하여 검증
      const itemName = (item.id === editingItemId && !finalItems) ? editingText.trim() : item.name;

      if (!itemName || !itemName.trim()) {
        emptyItems.push(item.id);
        hasEmptyFields = true;
      }
    });

    // 빈 항목이 있으면 경고 표시
    if (hasEmptyFields) {
      setInvalidItems(emptyItems);

      // 편집 중인 항목이 빈칸이면 포커스 유지
      if (editingItemId && emptyItems.includes(editingItemId)) {
        if (editInputRef.current) {
          editInputRef.current.focus();
        }
      }

      // 진동 피드백 (모바일 지원 시)
      if (navigator.vibrate) {
        navigator.vibrate(100);
      }

      // 경고는 유지 (사용자가 해당 필드 클릭하기 전까지)

      return; // 저장 중단
    }

    // 현재 표시된 순서대로 display_order를 0부터 재할당
    const finalReasons = itemsToSave.map((item, index) => {
      const itemName = (item.id === editingItemId && !finalItems) ? editingText.trim() : item.name;
      return {
        id: item.isNew ? null : item.id,
        text: itemName.trim(),
        originalText: itemName.trim(),
        display_order: index  // 0부터 시작하는 연속된 번호로 할당
      };
    });

    console.log('Saving call reasons with new order:', finalReasons);

    onReorder(finalReasons);
    setOriginalItems([...itemsToSave]); // 모든 항목 원본으로 업데이트

    // 저장 완료 후 닫기
    onClose();

    // 닫힌 후 토스트 팝업 표시
    setTimeout(() => {
      if (onShowToast) {
        onShowToast(`<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>호출 설정이 변경되었습니다`);
      }
    }, 300);
  };

  const handleCancelChanges = () => {
    // 변경사항 취소하고 원본으로 복원
    setItems([...originalItems]);
    onClose();
  };
  

  const handleEditClick = (item) => {
    // 인라인 편집 모드로 전환
    setEditingItemId(item.id);
    setEditingText(item.name);
    // 새 아이템인지 확인
    setIsNewItem(item.isNew || false);

    // input에 포커스를 주기 위해 약간의 지연
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        // 커서를 텍스트 끝으로 이동
        editInputRef.current.setSelectionRange(item.name.length, item.name.length);
      }
    }, 50);
  };

  const handleEditComplete = () => {
    if (editingItemId) {
      const index = items.findIndex(item => item.id === editingItemId);
      if (index !== -1) {
        // 기존 항목은 빈 값이어도 유지 (사용자가 명시적으로 삭제해야 함)
        const newItems = [...items];
        newItems[index] = {
          ...newItems[index],
          name: editingText.trim(),
          isNew: newItems[index].isNew
        };
        setItems(newItems);
        // 경고 표시 제거 (저장 버튼 누를 때만 표시)
      }
    }
    setEditingItemId(null);
    setEditingText('');
    setIsNewItem(false);
  };

  const handleEditCancel = () => {
    if (isNewItem && editingItemId) {
      // 새 아이템을 취소하면 제거
      const newItems = items.filter(item => item.id !== editingItemId);
      setItems(newItems);
    }
    setEditingItemId(null);
    setEditingText('');
    setIsNewItem(false);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditComplete();
      // 모바일 키보드 닫기
      if (editInputRef.current) {
        editInputRef.current.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };
  
  // input에서 포커스가 빠져나갈 때 호출되는 함수 수정
  const handleEditBlur = (e) => {
    // 포커스가 빠지면 편집 완료
    // relatedTarget이 다른 call-name인지 확인
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isEditingAnotherItem = activeElement &&
        (activeElement.classList.contains('call-edit-input') ||
         activeElement.closest('.call-name'));

      if (editingItemId && !isEditingAnotherItem) {
        handleEditComplete();
      }
    }, 50);
  };

  const handleDeleteClick = (item) => {
    // 이미 삭제 중이면 무시
    if (deletingItemId) return;

    // 해당 요소 찾기
    const itemIndex = items.findIndex(i => i.id === item.id);
    if (itemIndex === -1 || !itemRefs.current[itemIndex]) return;

    const element = itemRefs.current[itemIndex];

    // transition 즉시 제거
    element.style.transition = 'none';

    // 브라우저 리플로우 강제 실행
    void element.offsetHeight;

    // 삭제 애니메이션 시작
    setDeletingItemId(item.id);

    // 애니메이션 후 UI에서만 삭제 (저장 버튼 누르기 전까지는 임시)
    setTimeout(() => {
      const newItems = items.filter(i => i.id !== item.id);
      setItems(newItems);
      setDeletingItemId(null);
    }, 300); // 애니메이션 시간과 동일
  };


  // 드래그 관련 함수들
  const handleItemDragStart = (e, index) => {
    // 드래그 시작 시에만 기본 동작 방지
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setItemDragStartY(clientY);
    setDraggedItem(index);
    setOriginalIndex(index);
    setIsDraggingItem(true);
    // 초기 스크롤 위치 저장
    initialScrollTop.current = formContainerRef.current?.scrollTop || 0;
    accumulatedScroll.current = 0;
    // 드래그가 시작되면 스크롤 방지
    e.preventDefault();
    e.stopPropagation();
  };

  const handleItemDragMove = (e) => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const draggedElement = itemRefs.current[originalIndex];
    if (!draggedElement) return;

    // 현재 스크롤 위치와 초기 스크롤 위치의 차이 계산
    const currentScrollTop = formContainerRef.current?.scrollTop || 0;
    const scrollDelta = currentScrollTop - initialScrollTop.current;
    
    // 현재 드래그 위치 계산 (스크롤 변화량 보정)
    const deltaY = clientY - itemDragStartY + scrollDelta;
    
    // 드래그 중인 아이템 이동
    draggedElement.style.transform = `translateY(${deltaY}px)`;
    draggedElement.style.zIndex = '1000';
    draggedElement.style.position = 'relative';

    // 스크롤이 필요한 경우 자동 스크롤
    const containerRect = formContainerRef.current.getBoundingClientRect();
    const dragY = clientY - containerRect.top;
    
    // 자동 스크롤 함수
    const startAutoScroll = (direction, speed) => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
      
      const scroll = () => {
        if (!isDraggingItem || !formContainerRef.current) {
          cancelAnimationFrame(autoScrollRef.current);
          return;
        }
        
        const container = formContainerRef.current;
        const draggedElement = itemRefs.current[originalIndex];
        
        if (direction === 'up' && container.scrollTop > 0) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.max(0, container.scrollTop - speed);
          const scrollChange = oldScrollTop - container.scrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY - scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        } else if (direction === 'down' && container.scrollTop < container.scrollHeight - container.clientHeight) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.min(
            container.scrollHeight - container.clientHeight,
            container.scrollTop + speed
          );
          const scrollChange = container.scrollTop - oldScrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY + scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        }
      };
      
      autoScrollRef.current = requestAnimationFrame(scroll);
    };
    
    // 상단 근처에서 위로 스크롤
    if (dragY < 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (100 - dragY) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('up', scrollSpeed);
    } 
    // 하단 근처에서 아래로 스크롤
    else if (dragY > containerRect.height - 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (dragY - (containerRect.height - 100)) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('down', scrollSpeed);
    }
    // 중간 영역에서는 스크롤 중지
    else {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    // 드래그 중인 아이템의 현재 위치 계산
    const draggedRect = draggedElement.getBoundingClientRect();
    const draggedMidY = draggedRect.top + draggedRect.height / 2;
    
    let newIndex = originalIndex;
    
    // 드래그 중인 아이템이 어느 위치에 있는지 확인
    for (let i = 0; i < items.length; i++) {
      if (i === originalIndex) continue;
      
      const targetElement = itemRefs.current[i];
      if (!targetElement) continue;
      
      const targetRect = targetElement.getBoundingClientRect();
      
      // 원본 위치보다 아래로 드래그하는 경우
      if (originalIndex < i) {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심을 넘어갔을 때
        if (draggedMidY > targetRect.top + targetRect.height / 2) {
          newIndex = i;
        }
      } 
      // 원본 위치보다 위로 드래그하는 경우
      else {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심보다 위에 있을 때
        if (draggedMidY < targetRect.top + targetRect.height / 2) {
          newIndex = i;
          break;
        }
      }
    }
    
    // 위치가 변경되었을 때만 업데이트
    if (newIndex !== draggedItem) {
      setDraggedItem(newIndex);
      
      // 다른 아이템들 애니메이션
      const draggedElement = itemRefs.current[originalIndex];
      const itemHeight = draggedElement ? draggedElement.offsetHeight : 57;
      itemRefs.current.forEach((ref, index) => {
        if (ref && index !== originalIndex) {
          ref.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          
          let offset = 0;
          if (originalIndex < newIndex) {
            // 원본이 위에서 아래로
            if (index > originalIndex && index <= newIndex) {
              offset = -itemHeight;
            } else {
              offset = 0;
            }
          } else if (originalIndex > newIndex) {
            // 원본이 아래에서 위로
            if (index >= newIndex && index < originalIndex) {
              offset = itemHeight;
            } else {
              offset = 0;
            }
          }
          
          ref.style.transform = `translateY(${offset}px)`;
        }
      });
    }
  };

  const handleItemDragEnd = () => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    // 드래그 종료 시 진동 피드백
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
    
    // 자동 스크롤 중지
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    
    // 실제로 아이템 순서 변경 (로컬 상태만 변경, DB 변경 없음)
    if (originalIndex !== draggedItem) {
      const newItems = [...items];
      const [removed] = newItems.splice(originalIndex, 1);
      newItems.splice(draggedItem, 0, removed);
      setItems(newItems);

      // onReorder 호출 제거 - 저장 버튼 클릭 시에만 호출
    }
    
    // 모든 요소의 스타일 초기화를 requestAnimationFrame으로 처리
    requestAnimationFrame(() => {
      // 먼저 모든 요소의 transition을 제거하고 원위치
      itemRefs.current.forEach((ref) => {
        if (ref) {
          ref.style.transition = 'none';
          ref.style.transform = '';
          ref.style.zIndex = '';
          ref.style.position = '';
        }
      });
      
      // 다음 프레임에서 transition 복원
      requestAnimationFrame(() => {
        itemRefs.current.forEach((ref) => {
          if (ref) {
            ref.style.transition = '';
          }
        });
      });
    });
    
    // 상태 초기화
    setDraggedItem(null);
    setOriginalIndex(null);
    setIsDraggingItem(false);
  };


  if (!isVisible) return null;

  return (
    <div className={`call-settings-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose}>
      <div 
        ref={bottomSheetRef}
        className={`call-settings-sheet ${isAnimating ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          ref={wrapperRef}
          className="call-settings-wrapper"
          style={{ 
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging && currentY > 50 ? 0.95 : 1
          }}
        >
        {/* 드래그 핸들 영역 */}
        <div 
          className="drag-handle-area"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="drag-handle" />
        </div>

        {/* 헤더 섹션 */}
        <div className="header-section">
          <h2 className="header-title">호출 설정</h2>
          <div className="sub-text-container">
            {selectedLanguage === 'ko' ? (
                <>
                  <img
                    className="header-drag-icon"
                    src={adminHamburgerIcon}
                    alt="drag"
                    width="15"
                    height="11"
                  />
                  <span className="sub-text">위 아래로 순서를 바꿀 수 있어요!</span>
                </>
              ) : selectedLanguage === 'en' ? (
                <>
                  <img
                    className="header-flag-icon"
                    src={languageEnIcon}
                    alt="English"
                    width="20"
                    height="20"
                  />
                  <span className="sub-text">호출 내용을 영어로 번역해주세요!</span>
                </>
              ) : selectedLanguage === 'zh' ? (
                <>
                  <img
                    className="header-flag-icon"
                    src={languageCnIcon}
                    alt="Chinese"
                    width="20"
                    height="20"
                  />
                  <span className="sub-text">호출 내용을 중국어로 번역해주세요!</span>
                </>
              ) : selectedLanguage === 'ja' ? (
                <>
                  <img
                    className="header-flag-icon"
                    src={languageJaIcon}
                    alt="Japanese"
                    width="20"
                    height="20"
                  />
                  <span className="sub-text">호출 내용을 일본어로 번역해주세요!</span>
                </>
              ) : null}
          </div>
        </div>

        {/* 호출 사유 리스트 컨테이너 */}
        <div className="call-list-container">
          <>
              {/* 헤더 */}
              <div className="list-header">
                <span className="total-count">총 {items.length}개</span>
              </div>

              {/* 호출 사유 리스트 */}
              <div className="call-list" ref={formContainerRef}>
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`call-bar ${selectedLanguage !== 'ko' ? 'translate-mode' : ''} ${originalIndex === index && isDraggingItem ? 'dragging' : ''} ${deletingItemId === item.id ? 'deleting' : ''}`}
                    ref={el => itemRefs.current[index] = el}
                  >
                    {/* 햄버거 아이콘 - 한국어일 때만 표시 */}
                    {selectedLanguage === 'ko' && (
                      <div
                        className="drag-icon-wrapper"
                        onMouseDown={(e) => {
                          if ('vibrate' in navigator) {
                            navigator.vibrate(30);
                          }
                          handleItemDragStart(e, index);
                        }}
                        onTouchStart={(e) => {
                          if ('vibrate' in navigator) {
                            navigator.vibrate(30);
                          }
                          handleItemDragStart(e, index);
                        }}
                      >
                        <img
                          className="drag-icon"
                          src={adminHamburgerIcon}
                          alt="drag"
                          width="15"
                          height="11"
                        />
                      </div>
                    )}

                    {/* 호출 사유 이름 */}
                    <div
                      className={`call-name ${selectedLanguage !== 'ko' ? 'translate-mode' : ''}`}
                      onMouseDown={(e) => {
                        // mouseDown을 사용하여 blur보다 먼저 처리
                        if (editingItemId !== item.id) {
                          e.preventDefault(); // blur 방지
                          if (editingItemId) {
                            handleEditComplete(); // 현재 편집 중인 항목 완료
                          }
                          handleEditClick(item); // 새 항목 편집
                        }
                      }}
                      style={{ cursor: editingItemId !== item.id ? 'pointer' : 'default' }}
                    >
                      {editingItemId === item.id ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          className={`call-edit-input ${selectedLanguage !== 'ko' ? 'translate-mode' : ''} ${invalidItems.includes(item.id) ? 'invalid' : ''} ${item.isNew ? 'new-item' : ''}`}
                          value={editingText}
                          placeholder={selectedLanguage === 'ko' ? '호출명을 입력해주세요' : '번역을 입력해주세요'}
                          onChange={(e) => {
                            setEditingText(e.target.value);
                            // 값을 입력하면 빨간 테두리 제거
                            if (invalidItems.includes(item.id) && e.target.value.trim()) {
                              setInvalidItems(invalidItems.filter(id => id !== item.id));
                            }
                          }}
                          onKeyDown={handleEditKeyDown}
                          onBlur={handleEditBlur}
                          onClick={(e) => e.stopPropagation()}
                          enterKeyHint="done"
                          inputMode="text"
                        />
                      ) : (
                        <div className={`call-display-text ${selectedLanguage !== 'ko' ? 'translate-mode' : ''} ${!item.name && item.isNew ? 'placeholder' : ''} ${invalidItems.includes(item.id) ? 'invalid' : ''} ${item.isNew ? 'new-item' : ''}`}>
                          {item.name || (item.isNew ? (selectedLanguage === 'ko' ? '호출명을 입력해주세요' : '번역을 입력해주세요') : '')}
                        </div>
                      )}
                    </div>

                    {/* 삭제 버튼 - 한국어일 때만 표시 */}
                    {selectedLanguage === 'ko' && (
                      <button
                        className="call-action-btn delete-icon"
                        onClick={() => {
                          if (deletingItemId) return;
                          if (editingItemId === item.id) {
                            // 편집 중이면 편집 완료 처리
                            handleEditComplete();
                          }
                          // 모든 아이템에 대해 동일한 삭제 애니메이션 적용
                          setTimeout(() => {
                            handleDeleteClick(item);
                          }, 150);
                        }}
                      >
                        <img
                          src={adminDeleteIcon}
                          alt="삭제"
                          width="20"
                          height="20"
                        />
                      </button>
                    )}
                  </div>
                ))}


                {/* 추가 버튼 - 한국어일 때만 표시 */}
                {selectedLanguage === 'ko' && (
                  <button
                    className="add-call-bar"
                    onClick={(e) => {
                      // 편집 중이면 완료 처리
                      if (editingItemId) {
                        handleEditComplete();
                        // 편집 완료 후 추가 실행
                        setTimeout(() => {
                          handleAddClick(e);
                        }, 100);
                      } else {
                        handleAddClick(e);
                      }
                    }}
                  >
                    + 추가
                  </button>
                )}

                {/* 하단 여백 */}
                <div style={{ height: '40px' }} />
              </div>
            </>

          {/* 커스텀 스크롤바 */}
          {showScrollbar && (
            <div className="custom-scrollbar-track">
              <div 
                ref={scrollbarRef}
                className="custom-scrollbar-thumb"
                style={{ 
                  top: `${scrollbarTop}px`,
                  height: `${scrollbarHeight}px`
                }}
                onMouseDown={handleScrollbarMouseDown}
                onTouchStart={handleScrollbarMouseDown}
              />
            </div>
          )}
        </div>

        {/* 하단 버튼들 */}
        <div className="call-bottom-actions">
          <button className="call-action-button call-close-button" onClick={() => {
            setTimeout(() => {
              handleCancelChanges();
            }, 50);
          }}>
            닫기
          </button>
          <button className="call-action-button call-save-button" onClick={() => {
            // 편집 중인 경우 현재 편집 값을 포함하여 저장
            if (editingItemId) {
              const newItems = [...items];
              const index = items.findIndex(item => item.id === editingItemId);
              if (index !== -1) {
                newItems[index] = {
                  ...newItems[index],
                  name: editingText.trim(), // 빈 값도 포함
                  isNew: newItems[index].isNew
                };
              }
              handleSaveChanges(newItems);
            } else {
              handleSaveChanges();
            }
          }}>
            저장
          </button>
        </div>
        </div>
      </div>

      <style jsx="true">{`
        .call-settings-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          z-index: 1001;
          transition: background 0.2s ease;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }

        .call-settings-overlay.open {
          background: rgba(0, 0, 0, 0.5);
        }

        .call-settings-sheet {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(100%);
          width: 100%;
          height: 90%;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .call-settings-sheet.open {
          transform: translateY(0);
        }
        
        .call-settings-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          background: white;
          border-radius: 24px 24px 0 0;
          overflow: hidden;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          transform: translateZ(0);
          overscroll-behavior: contain;
          touch-action: pan-y;
          contain: layout style;
        }
        
        @media screen and (min-width: 361px) {
          .call-settings-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .call-settings-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .call-settings-wrapper {
            width: 360px;
            zoom: calc(100vw / 360);
          }
        }

        .drag-handle-area {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 48px;
          cursor: grab;
          touch-action: none;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 1;
          padding-top: 12px;
          box-sizing: border-box;
        }

        .drag-handle-area:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 52px;
          height: 4px;
          background: #E5E8EB;
          border-radius: 10px;
          transition: all 0.2s ease;
        }
        
        .call-settings-sheet.open .drag-handle {
          background: #E5E8EB;
        }
        
        .drag-handle-area:active .drag-handle {
          background: #C9CDD1;
          transform: scaleX(1.2);
        }

        .header-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid #F3F5F7;
          padding-bottom: 24px;
          padding-top: 40px;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          color: #2E2E2E;
          margin: 0;
          text-align: center;
        }
        
        .sub-text-container {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
        }
        
        .header-drag-icon {
          flex-shrink: 0;
        }

        .header-flag-icon {
          flex-shrink: 0;
          object-fit: cover;
        }

        .sub-text {
          font-size: 14px;
          font-weight: 500;
          color: #949494;
        }

        /* OKDC 미지원 메시지 */
        .okdc-not-supported {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 85%;
          padding: 40px 20px;
        }

        .okdc-message {
          font-size: 16px;
          font-weight: 500;
          color: #C3C3C3;
          text-align: center;
          line-height: 1.5;
        }

        .call-bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          gap: 8px;
          pointer-events: none;
        }
        
        .call-bottom-actions .call-action-button {
          pointer-events: auto;
        }

        .call-action-button {
          width: 164px;
          height: 50px;
          border-radius: 10px;
          border: none;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .call-close-button {
          background: #F1F1F1;
          color: #959595;
        }

        .call-close-button.full-width {
          width: 336px;
          height: 50px;
        }

        .call-save-button {
          background: #FDCD27;
          color: #FFFFFF;
        }

        .call-close-button:active,
        .call-save-button:active {
          animation: buttonPress 0.1s ease-out;
        }

        .call-list-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px 10px 20px;
        }
        
        .total-count {
          font-size: 12px;
          font-weight: 600;
          color: #888888;
        }
        
        
        .call-list {
          height: 100%;
          padding: 0 0 70px 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          position: relative;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }
        
        .call-list::-webkit-scrollbar {
          display: none;
        }
        
        /* 커스텀 스크롤바 스타일 */
        .custom-scrollbar-track {
          position: absolute;
          right: 0px;
          top: 3px;
          bottom: 70px;
          width: 6px;
          z-index: 100;
        }
        
        .custom-scrollbar-thumb {
          position: absolute;
          right: 0;
          width: 6px;
          background: rgb(201, 201, 201);
          border-radius: 3px;
          cursor: grab;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar-thumb:hover {
          
        }
        
        .custom-scrollbar-thumb:active {
          cursor: grabbing;
          
        }

        .call-bar {
          display: flex;
          align-items: center;
          height: 72px;
          padding: 10px 20px 10px 10px;
          position: relative;
          background: white;
          user-select: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        /* 번역 모드 - 중앙 정렬 */
        .call-bar.translate-mode {
          justify-content: center;
          padding: 10px 20px 12px 20px;
          height: 76px;
        }

        .call-bar.dragging {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          transition: none;
          z-index: 1000;
        }
        
        .call-bar.deleting {
          animation: slideOutAndCollapse 0.3s ease-out forwards;
          transition: none;
        }
        
        @keyframes slideOutAndCollapse {
          0% {
            transform: translateX(0);
            opacity: 1;
            height: 72px;
            padding: 10px 20px 10px 10px;
          }
          50% {
            transform: translateX(-100%);
            opacity: 0;
            height: 72px;
            padding: 10px 20px 10px 10px;
          }
          100% {
            transform: translateX(-100%);
            opacity: 0;
            height: 0;
            padding: 0;
            margin: 0;
          }
        }
        
        .drag-icon-wrapper {
          flex-shrink: 0;
          cursor: grab;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          padding: 30px 10px;
          margin: -15px -10px -15px 0;
          touch-action: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .drag-icon-wrapper:active {
          cursor: grabbing;
        }
        
        .drag-icon {
          pointer-events: none;
        }
        
        .call-name {
          position: relative;
          margin-left: 20px;
          display: flex;
          align-items: center;
          flex: 1;
        }

        /* 번역 모드 - margin 제거 */
        .call-name.translate-mode {
          margin-left: 0;
          flex: none;
        }

        .call-edit-input {
          width: 245px;
          height: 52px;
          padding: 12px 16px;
          border: 1.2px solid #D9D9D9;
          border-radius: 8px;
          outline: none;
          background: #FFFFFF;
          font-size: 17px;
          font-weight: 600;
          color: #282828;
          font-family: inherit;
          caret-color: #FDCD27;
          box-sizing: border-box;
          transition: border-color 0.2s ease;
        }

        .call-edit-input.new-item {
          border-color: #E8E8E8;
        }

        .call-edit-input:focus {
          border-color: #FDCD27;
        }

        .call-edit-input::placeholder {
          color: #CBCBCB;
          font-weight: 500;
          font-size: 17px;
        }

        .call-edit-input.invalid {
          border-color: #F41717;
          animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        /* 번역 모드 - 입력 필드 320px */
        .call-edit-input.translate-mode {
          width: 320px;
        }

        .call-display-text {
          width: 245px;
          height: 52px;
          padding: 14px 16px;
          border: 1.2px solid #D9D9D9;
          border-radius: 8px;
          background: #FFFFFF;
          font-size: 17px;
          font-weight: 600;
          color: #282828;
          display: flex;
          align-items: center;
          box-sizing: border-box;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .call-display-text.new-item {
          border-color: #E8E8E8;
        }

        /* 번역 모드 - 표시 텍스트 320px */
        .call-display-text.translate-mode {
          width: 320px;
        }

        .call-display-text:hover {
          background-color: #FAFAFB;
        }

        .call-display-text:focus {
          outline: none;
          border-color: #FDCD27;
        }

        .call-display-text.placeholder {
          color: #CBCBCB;
          font-weight: 500;
        }

        .call-display-text.invalid {
          border-color: #F41717;
          animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        @keyframes shake {
          0% { transform: translateX(0); }
          12.5% { transform: translateX(-1px); }
          25% { transform: translateX(1px); }
          37.5% { transform: translateX(-1px); }
          50% { transform: translateX(1px); }
          62.5% { transform: translateX(-1px); }
          75% { transform: translateX(1px); }
          87.5% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
        
        
        .call-action-btn {
          width: 49px;
          height: 29px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          margin-left: auto;
        }
        
        .call-action-btn.edit {
          background: #F1F1F1;
          color: #666666;
        }
        
        .call-action-btn.delete-icon {
          background: transparent;
          border: none;
          padding: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-left: 20px;
        }
        
        .call-action-btn.delete-icon:active {
          opacity: 0.6;
        }
        
        .add-button {
          width: calc(100% + 40px);
          height: 56px;
          margin: 0 -20px;
          border: none;
          background: #FFFFFF;
          color: #959595;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        

        .bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          pointer-events: none;
        }
        
        .bottom-actions .action-button {
          pointer-events: auto;
        }

        .action-button {
          width: 328px;
          height: 50px;
          border-radius: 14px;
          border: none;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
        }

        .close-button {
          background: #F1F1F1;
          color: #959595;
        }

        .close-button:active {
          animation: buttonPress 0.1s ease-out;
        }

        
        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(0.92);
          }
          100% {
            transform: scale(1);
          }
        }

        .add-call-bar {
          width: 100%;
          height: 43px;
          background: #FFFFFF;
          border: none;
          font-size: 16px;
          font-weight: bold;
          color: #959595;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0 20px;
          padding-top: 6px;
          margin-bottom: 10px;
          transition: background-color 0.2s ease;
          position: relative;
          overflow: hidden;
        }


        .add-call-bar .ripple {
          position: absolute;
          border-radius: 50%;
          background: rgba(253, 205, 39, 0.4);
          transform: scale(0);
          animation: ripple-animation 0.6s ease-out;
          pointer-events: none;
        }

        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CallSettingsBottomSheet;